import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Megaphone, Copy, Check, ShieldQuestion, Sparkles, Pencil, Rocket, Loader2, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

function CopyBtn({ text, id }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
    }
    setDone(true);
    toast.success("Copied!");
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <button onClick={copy} data-testid={`copy-${id}`} title="Copy"
      className="shrink-0 rounded-full border border-border p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-primary">
      {done ? <Check size={15} className="text-accent" /> : <Copy size={15} />}
    </button>
  );
}

export default function PitchTest() {
  const navigate = useNavigate();
  const { pid } = useParams();
  const { refreshProfile, refreshCredits } = useAuth();
  const [profile, setProfile] = useState(null);
  const [pitch, setPitch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const run = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/offer-profiles/${pid}/pitch-test`);
      setPitch(data.pitch_test);
      await Promise.all([refreshProfile(), refreshCredits()]);
    } catch (e) {
      if (e.response?.status !== 402)
        toast.error(e.response?.data?.detail || "Could not generate the Pitch Test. Try again.");
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [p, pt] = await Promise.all([
          api.get(`/offer-profiles/${pid}`),
          api.get(`/offer-profiles/${pid}/pitch-test`),
        ]);
        if (!active) return;
        setProfile(p.data);
        if (pt.data.done && pt.data.pitch_test) {
          setPitch(pt.data.pitch_test);
          setLoading(false);
        } else {
          setLoading(false);
          run(); // auto-generate on first visit
        }
      } catch (e) {
        toast.error("Could not load that profile");
        navigate("/offer-profiles");
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  return (
    <div className="mx-auto max-w-3xl space-y-8" data-testid="pitch-test-page">
      <div className="fade-up flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <Megaphone size={24} />
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Pitch Test</p>
          <h1 className="font-head text-4xl font-bold">Does your offer sell?</h1>
          <p className="mt-1 text-muted-foreground">
            {profile?.name ? <>A quick gut-check for <span className="font-semibold text-foreground">{profile.name}</span> before you build the course.</>
              : "A quick gut-check before you build the course."}
          </p>
        </div>
      </div>

      {loading || generating || !pitch ? (
        <Card className="flex flex-col items-center gap-3 border-dashed p-16 text-center" data-testid="pitch-loading">
          <Loader2 size={28} className="animate-spin text-primary" />
          <p className="font-head text-lg font-semibold">Writing your pitch…</p>
          <p className="text-sm text-muted-foreground">Testing your offer with a direct-response copywriter.</p>
        </Card>
      ) : (
        <div className="space-y-6 fade-up">
          {/* Headline + subheadline */}
          <Card className="border-border p-6" data-testid="pitch-headline-card">
            <div className="mb-3 flex items-center justify-between">
              <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Sparkles size={14} className="text-primary" /> Headline + subheadline
              </span>
              <CopyBtn id="headline" text={`${pitch.headline}\n${pitch.subheadline}`} />
            </div>
            <h2 className="font-head text-2xl font-bold leading-tight" data-testid="pitch-headline">{pitch.headline}</h2>
            <p className="mt-2 text-muted-foreground" data-testid="pitch-subheadline">{pitch.subheadline}</p>
          </Card>

          {/* Sales pitch */}
          <Card className="border-border p-6" data-testid="pitch-pitch-card">
            <div className="mb-3 flex items-center justify-between">
              <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Megaphone size={14} className="text-primary" /> Sales pitch
              </span>
              <CopyBtn id="pitch" text={pitch.pitch} />
            </div>
            <p className="leading-relaxed" data-testid="pitch-pitch">{pitch.pitch}</p>
          </Card>

          {/* Objections */}
          <div>
            <h3 className="mb-3 flex items-center gap-2 font-head text-xl font-bold">
              <ShieldQuestion size={18} className="text-primary" /> Objections &amp; responses
            </h3>
            <div className="space-y-3">
              {pitch.objections.map((o, i) => (
                <Card key={o.objection || i} className="border-border p-5" data-testid={`pitch-objection-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-semibold">“{o.objection}”</p>
                    <CopyBtn id={`objection-${i}`} text={`Objection: ${o.objection}\nResponse: ${o.response}`} />
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    <span className="font-medium text-accent">Response:</span> {o.response}
                  </p>
                </Card>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => navigate(`/offer-profiles/${pid}`)}
                data-testid="refine-profile-btn" className="rounded-full">
                <Pencil size={16} className="mr-1.5" /> Refine My Profile
              </Button>
              <Button variant="ghost" onClick={run} disabled={generating}
                data-testid="regenerate-pitch-btn" className="rounded-full text-muted-foreground">
                <RefreshCw size={16} className="mr-1.5" /> Regenerate · 5 credits
              </Button>
            </div>
            <Button onClick={() => navigate(`/campaigns/new?profile=${pid}`)}
              data-testid="build-course-btn"
              className="rounded-full bg-primary px-6 py-6 text-base font-semibold hover:bg-primary/90">
              <Rocket size={18} className="mr-1.5" /> Build My Course
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
